import {
  Anime,
  AnimeSeason,
  AnimeSource,
  BroadcastDay,
  BROADCAST_DAYS,
  PlatformType,
  WakuFeedItem,
} from '../types/anime';

export const DEFAULT_ANIME_COVER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='400' viewBox='0 0 300 400'%3E%3Crect width='300' height='400' fill='%230f172a'/%3E%3Ctext x='50%25' y='45%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='48' fill='%23475569'%3E🎬%3C/text%3E%3Ctext x='50%25' y='58%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='14' fill='%2364748b'%3EAnime Cover%3C/text%3E%3C/svg%3E";

export function getTodayKey(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getTodayBroadcastDay(): BroadcastDay {
  const jsDay = new Date().getDay(); // 0 = Sun, 1 = Mon, ...
  const found = BROADCAST_DAYS.find((d) => d.jsDayIndex === jsDay);
  return found ? found.id : 'SAT';
}

export function getCurrentSeasonAndYear(): { season: AnimeSeason; year: number } {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  if (month >= 1 && month <= 3) return { season: 'WINTER', year };
  if (month >= 4 && month <= 6) return { season: 'SPRING', year };
  if (month >= 7 && month <= 9) return { season: 'SUMMER', year };
  return { season: 'FALL', year };
}

export function isUpdatedToday(anime: Anime): boolean {
  if (!anime.lastUpdatedAt) return false;
  return anime.lastUpdatedAt === getTodayKey();
}

/** On AnimeRuka's "งดฉาย / พักฉาย" list and its return date hasn't come yet */
export function isOnBreak(anime: Pick<Anime, 'onBreak'>, today = getTodayKey()): boolean {
  return Boolean(anime.onBreak && (!anime.onBreak.returnOn || today < anime.onBreak.returnOn));
}

export function isAiringToday(anime: Anime): boolean {
  if (anime.status === 'จบแล้ว' || isOnBreak(anime)) return false;
  return anime.broadcastDay === getTodayBroadcastDay();
}

const YT_THUMB_TO_MAL_POSTER: Record<string, string> = {
  // Fall 2026
  pEmW0fU89yE: 'https://cdn.myanimelist.net/images/anime/1478/160268l.jpg',
  EcjBNO9QFEg: 'https://cdn.myanimelist.net/images/anime/1295/158726l.jpg',
  q4mX4PIIpzQ: 'https://cdn.myanimelist.net/images/anime/1963/160339l.jpg',
  tICPI_7ol9U: 'https://cdn.myanimelist.net/images/anime/1760/159941l.jpg',
  '8ajwBuWf-C4': 'https://cdn.myanimelist.net/images/anime/1360/156303l.jpg',
  accDkw7T7BU: 'https://cdn.myanimelist.net/images/anime/1135/159762l.jpg',
  jH3OG1y2VZo: 'https://cdn.myanimelist.net/images/anime/1465/153763l.jpg',
  fPI7JCdx8_o: 'https://cdn.myanimelist.net/images/anime/1874/160335l.jpg',
  'tbjuv-4yZH0': 'https://cdn.myanimelist.net/images/anime/1190/159644l.jpg',
  'GQx7Tyk3-9I': 'https://cdn.myanimelist.net/images/anime/1618/160107l.jpg',
  iJc9hyx8NqQ: 'https://cdn.myanimelist.net/images/anime/1518/160127l.jpg',
  LkuR5TFFb94: 'https://cdn.myanimelist.net/images/anime/1928/160253l.jpg',
  // Thai Dub & Summer 2026
  'PLBds-1fW7mjc': 'https://cdn.myanimelist.net/images/anime/5/82890l.jpg',
  'roWpLbDsy-c': 'https://cdn.myanimelist.net/images/anime/1275/158595l.jpg',
  ni1B13G8jCo: 'https://cdn.myanimelist.net/images/anime/1219/154541l.jpg',
  '1gAVsfBeHsg': 'https://cdn.myanimelist.net/images/anime/1352/155195l.jpg',
  DtbBi4fyZN0: 'https://cdn.myanimelist.net/images/anime/1809/158105l.jpg',
  PLRBp2XaX4C50: 'https://cdn.myanimelist.net/images/anime/1140/154457l.jpg',
  '7XT8tz_cP5A': 'https://cdn.myanimelist.net/images/anime/1917/158371l.jpg',
  QO0ck04fBtg: 'https://cdn.myanimelist.net/images/anime/1343/95287l.jpg',
  fTy2ayGRORo: 'https://cdn.myanimelist.net/images/anime/5/82890l.jpg',
  M89mnPi5sEs: 'https://cdn.myanimelist.net/images/anime/1538/148604l.jpg',
  JFlJUQEwtlI: 'https://cdn.myanimelist.net/images/anime/1064/155042l.jpg',
  PLZEGY19mpcO0: 'https://cdn.myanimelist.net/images/anime/1812/134736l.jpg',
  '3hVkD9N1c70': 'https://cdn.myanimelist.net/images/anime/1681/154996l.jpg',
  iiVjs3IteWo: 'https://cdn.myanimelist.net/images/anime/10/73274l.jpg',
  OknXm8gUK4U: 'https://cdn.myanimelist.net/images/anime/4/50361l.jpg',
  JFNXJlXhqqI: 'https://cdn.myanimelist.net/images/anime/1665/140360l.jpg',
  _nQ8zRVgx6Y: 'https://cdn.myanimelist.net/images/anime/1764/155312l.jpg',
  '1mRlROJy9G8': 'https://cdn.myanimelist.net/images/anime/1370/140362l.jpg',
  uH6H9oIsVlU: 'https://cdn.myanimelist.net/images/anime/1177/150344l.jpg',
  LjT4l43eZ1g: 'https://cdn.myanimelist.net/images/anime/1589/156720l.jpg',
  UUbzW1CaiMo: 'https://cdn.myanimelist.net/images/anime/1056/154990l.jpg',
  aWsvvuoJEwc: 'https://cdn.myanimelist.net/images/anime/1370/140362l.jpg',
  e9hg78Dg0Dc: 'https://cdn.myanimelist.net/images/anime/1908/135431l.jpg',
  '7ipnzCBNfBU': 'https://cdn.myanimelist.net/images/anime/1910/158471l.jpg',
  boOVPdhGTdg: 'https://cdn.myanimelist.net/images/anime/1917/158371l.jpg',
  '2PbUt7g06TU': 'https://cdn.myanimelist.net/images/anime/1878/157796l.jpg',
  '5JV-Cvy0w3I': 'https://cdn.myanimelist.net/images/anime/1140/154457l.jpg',
  VTYytYOdwPY: 'https://cdn.myanimelist.net/images/anime/1255/150593l.jpg',
  qGYuhsrzOL0: 'https://cdn.myanimelist.net/images/anime/1417/157798l.jpg',
  SNK7EK_CVC4: 'https://cdn.myanimelist.net/images/anime/1145/158339l.jpg',
  'ktF-NYSHPyo': 'https://cdn.myanimelist.net/images/anime/1809/158105l.jpg',
  UECxCO1XzBk: 'https://cdn.myanimelist.net/images/anime/1098/158743l.jpg',
  'nei-0mBq7Lc': 'https://cdn.myanimelist.net/images/anime/1491/157799l.jpg',
};

export function formatImageUrl(rawUrl?: string): string {
  if (!rawUrl || !rawUrl.trim()) return DEFAULT_ANIME_COVER;
  let url = rawUrl.trim();
  if (url.startsWith('data:image/')) return url;
  if (url.startsWith('//')) url = 'https:' + url;

  // Automatically upgrade ugly YouTube horizontal thumbnails to official vertical MAL Key Visual Posters
  if (url.includes('i.ytimg.com')) {
    const viMatch = url.match(/\/vi\/([^/]+)\//) || url.match(/\/tvfilm_banner\/([^/]+)\//);
    if (viMatch && YT_THUMB_TO_MAL_POSTER[viMatch[1]]) {
      return YT_THUMB_TO_MAL_POSTER[viMatch[1]];
    }
  }

  // Bilibili covers are full-size 1–4MB PNGs; ask the CDN for a ~10KB resized WebP instead
  if (/^https:\/\/p(?:ic)?\.bstarstatic\.com\/ogv\/[^@?]+\.(?:png|jpe?g|webp)$/i.test(url)) {
    return `${url}@400w.webp`;
  }

  // Google Drive Link conversion
  const driveMatch =
    url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) || url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (driveMatch && driveMatch[1]) {
    return `https://lh3.googleusercontent.com/d/${driveMatch[1]}=w800`;
  }

  return url;
}

export function detectPlatformFromUrl(url: string): { type: PlatformType; name: string } {
  const lower = url.toLowerCase();
  if (lower.includes('youtube.com') || lower.includes('youtu.be')) {
    if (lower.includes('muse')) return { type: 'youtube', name: 'YouTube (Muse)' };
    if (lower.includes('anione') || lower.includes('ani-one'))
      return { type: 'youtube', name: 'YouTube (Ani-One)' };
    return { type: 'youtube', name: 'YouTube' };
  }
  if (lower.includes('anime-waku.com')) {
    return { type: 'anime-waku', name: 'Anime-Waku' };
  }
  if (lower.includes('animeruka.com')) {
    return { type: 'animeruka', name: 'AnimeRuka' };
  }
  if (lower.includes('bilibili.tv') || lower.includes('bilibili.com')) {
    return { type: 'bilibili', name: 'Bilibili' };
  }
  if (lower.includes('wetv.vip') || lower.includes('wetv.qq.com')) {
    return { type: 'wetv', name: 'WeTV' };
  }
  if (lower.includes('netflix.com')) {
    return { type: 'netflix', name: 'Netflix' };
  }
  if (lower.includes('iq.com') || lower.includes('iqiyi.com')) {
    return { type: 'iqiyi', name: 'iQIYI' };
  }
  if (lower.includes('crunchyroll.com')) {
    return { type: 'crunchyroll', name: 'Crunchyroll' };
  }
  try {
    const u = new URL(url);
    return { type: 'other', name: u.hostname.replace(/^www\./, '') };
  } catch {
    return { type: 'other', name: 'เว็บไซต์หลัก' };
  }
}

export interface AnimePlatformSlots {
  aniOne?: { url: string; label: string; icon: string; bg: string; text: string; border: string };
  bilibili?: { url: string; label: string; icon: string; bg: string; text: string; border: string };
  waku?: { url: string; label: string; icon: string; bg: string; text: string; border: string };
  ruka?: { url: string; label: string; icon: string; bg: string; text: string; border: string };
}

export function getAnimePlatformSlots(anime: Anime): AnimePlatformSlots {
  let aniOne: AnimePlatformSlots['aniOne'];
  let bilibili: AnimePlatformSlots['bilibili'];
  let waku: AnimePlatformSlots['waku'];
  let ruka: AnimePlatformSlots['ruka'];

  for (const s of anime.sources || []) {
    const url = (s.url || '').toLowerCase();
    const name = (s.name || '').toLowerCase();

    // 1. Ani-One Thailand (YouTube)
    if (
      !aniOne &&
      (url.includes('anione') ||
        url.includes('ani-one') ||
        name.includes('anione') ||
        name.includes('ani-one') ||
        s.type === 'youtube')
    ) {
      aniOne = {
        url: s.url,
        label: 'Ani-One',
        icon: '▶️',
        bg: 'bg-red-500/20 hover:bg-red-500/35',
        text: 'text-red-300',
        border: 'border-red-500/50',
      };
      continue;
    }

    // 2. Bilibili
    if (
      !bilibili &&
      (s.type === 'bilibili' || url.includes('bilibili') || name.includes('bilibili'))
    ) {
      bilibili = {
        url: s.url,
        label: 'Bilibili',
        icon: '📺',
        bg: 'bg-sky-500/20 hover:bg-sky-500/35',
        text: 'text-sky-300',
        border: 'border-sky-500/50',
      };
      continue;
    }

    // 3. Anime-Waku
    if (
      !waku &&
      (s.type === 'anime-waku' || url.includes('anime-waku') || name.includes('waku'))
    ) {
      waku = {
        url: s.url,
        label: 'Waku',
        icon: '🌐',
        bg: 'bg-teal-500/20 hover:bg-teal-500/35',
        text: 'text-teal-300',
        border: 'border-teal-500/50',
      };
      continue;
    }

    // 4. AnimeRuka
    if (!ruka && (s.type === 'animeruka' || url.includes('animeruka'))) {
      ruka = {
        url: s.url,
        label: 'Ruka',
        icon: '🟠',
        bg: 'bg-orange-500/20 hover:bg-orange-500/35',
        text: 'text-orange-300',
        border: 'border-orange-500/50',
      };
      continue;
    }
  }

  return { aniOne, bilibili, waku, ruka };
}

const SHORT_DAY_NAMES: Record<string, string> = {
  MON: 'วันจันทร์',
  TUE: 'วันอังคาร',
  WED: 'วันพุธ',
  THU: 'วันพฤหัส',
  FRI: 'วันศุกร์',
  SAT: 'วันเสาร์',
  SUN: 'วันอาทิตย์',
  UNKNOWN: 'ไม่ระบุวัน',
};

export function getAnimeScheduleSubtitle(anime: Anime): {
  text: string;
  isEstimated: boolean;
  dayShort: string;
  timeStr: string;
  chapterStr: string;
} {
  const slots = getAnimePlatformSlots(anime);
  const hasOfficialSource = Boolean(slots.aniOne || slots.bilibili);
  const isEstimated = !hasOfficialSource;

  const dayShort = SHORT_DAY_NAMES[anime.broadcastDay] || 'วันพฤหัส';
  const timeStr = anime.broadcastTime || (isEstimated ? '20:00' : '20:00');
  const epNum = anime.latestEp || 1;
  const chapterStr = `บทที่ ${epNum}`;

  let text = '';
  if (isEstimated) {
    text = `${dayShort} ${timeStr} (คาดการณ์) • ${chapterStr}`;
  } else {
    text = `${dayShort} ${timeStr} • ${chapterStr}`;
  }

  return { text, isEstimated, dayShort, timeStr, chapterStr };
}

export function getSourceBadgeInfo(source: { type: PlatformType; name?: string; url?: string }): {
  label: string;
  icon: string;
  bg: string;
  text: string;
  border: string;
} {
  const url = (source.url || '').toLowerCase();
  const name = (source.name || '').toLowerCase();

  if (url.includes('anione') || url.includes('ani-one') || name.includes('anione') || name.includes('ani-one')) {
    return {
      label: 'Ani-One',
      icon: '▶️',
      bg: 'bg-red-500/20 hover:bg-red-500/30',
      text: 'text-red-300',
      border: 'border-red-500/50',
    };
  }
  if (url.includes('muse') || name.includes('muse')) {
    return {
      label: 'Muse',
      icon: '▶️',
      bg: 'bg-amber-500/20 hover:bg-amber-500/30',
      text: 'text-amber-300',
      border: 'border-amber-500/50',
    };
  }
  if (source.type === 'bilibili' || url.includes('bilibili') || name.includes('bilibili')) {
    return {
      label: 'Bilibili',
      icon: '📺',
      bg: 'bg-sky-500/20 hover:bg-sky-500/30',
      text: 'text-sky-300',
      border: 'border-sky-500/50',
    };
  }
  if (source.type === 'anime-waku' || url.includes('anime-waku') || name.includes('waku')) {
    return {
      label: 'Waku',
      icon: '🌐',
      bg: 'bg-teal-500/20 hover:bg-teal-500/30',
      text: 'text-teal-300',
      border: 'border-teal-500/50',
    };
  }
  if (source.type === 'animeruka' || url.includes('animeruka')) {
    return {
      label: 'Ruka',
      icon: '🟠',
      bg: 'bg-orange-500/20 hover:bg-orange-500/30',
      text: 'text-orange-300',
      border: 'border-orange-500/50',
    };
  }
  if (source.type === 'wetv' || url.includes('wetv') || name.includes('wetv')) {
    return {
      label: 'WeTV',
      icon: '🟢',
      bg: 'bg-emerald-500/20 hover:bg-emerald-500/30',
      text: 'text-emerald-300',
      border: 'border-emerald-500/50',
    };
  }
  if (source.type === 'youtube' || url.includes('youtube') || url.includes('youtu.be')) {
    return {
      label: 'Ani-One',
      icon: '▶️',
      bg: 'bg-red-500/20 hover:bg-red-500/30',
      text: 'text-red-300',
      border: 'border-red-500/50',
    };
  }
  return {
    label: source.name || 'เว็บหลัก',
    icon: '🔗',
    bg: 'bg-slate-800 hover:bg-slate-700',
    text: 'text-slate-300',
    border: 'border-slate-700',
  };
}

/** Audio track a source link serves. Waku dub pages have a "-th" slug; others say พากย์ in the name. */
export function getSourceAudio(src: {
  url?: string;
  name?: string;
  audio?: string;
}): 'ซับไทย' | 'พากย์ไทย' {
  if (src.audio === 'ซับไทย' || src.audio === 'พากย์ไทย') return src.audio;
  let url = src.url || '';
  try {
    url = decodeURIComponent(url);
  } catch {}
  if (/anime-waku\.com\/anime\/[^/]*-th(?:-[^/]*)?\/?$/i.test(url)) return 'พากย์ไทย';
  if (/พากย์/.test(src.name || '')) return 'พากย์ไทย';
  return 'ซับไทย';
}

/** Whether the anime still links the website an episode came from */
export function episodeHasSource(ep: { url: string }, sources: { url: string }[]): boolean {
  const site = detectPlatformFromUrl(ep.url);
  return sources.some((s) => {
    const d = detectPlatformFromUrl(s.url);
    return d.type === site.type && (site.type !== 'other' || d.name === site.name);
  });
}

/**
 * Drop the saved episodes of websites no longer linked from the anime, and bring the latest
 * episode number back to what the remaining episodes show.
 */
export function pruneEpisodesToSources(anime: Anime): Anime {
  const episodes = anime.episodes || [];
  const kept = episodes.filter((ep) => episodeHasSource(ep, anime.sources));
  if (kept.length === episodes.length) return anime;
  const latestKept = kept.reduce((m, ep) => Math.max(m, ep.number), 0);
  return {
    ...anime,
    episodes: kept,
    latestEp: latestKept > 0 ? Math.min(anime.latestEp, Math.max(latestKept, anime.watchedEp)) : anime.latestEp,
  };
}

/** Same link, or same platform + same audio track. A sub page and a dub page are both kept. */
export function isDuplicateSource(
  a: { type?: string; url?: string; name?: string; audio?: string },
  b: { type?: string; url?: string; name?: string; audio?: string }
): boolean {
  if (a.url && b.url && a.url === b.url) return true;
  return a.type === b.type && getSourceAudio(a) === getSourceAudio(b);
}

/**
 * A saved anime that already links a *different* page on the same platform + audio track is
 * another part/season (Bleach TYBW Part 1 vs Calamity), however similar the titles are.
 */
export function hasConflictingSource(
  anime: { sources?: { type?: string; url?: string; name?: string; audio?: string }[] },
  item: { url: string; platform?: string; audioType?: string }
): boolean {
  const itemSource = {
    type: item.platform || detectPlatformFromUrl(item.url).type,
    url: item.url,
    name: item.audioType === 'พากย์ไทย' ? 'พากย์ไทย' : '',
  };
  return (anime.sources || []).some(
    (s) => s.url !== item.url && isDuplicateSource(s, itemSource)
  );
}

/**
 * A link counting far more episodes than this season has — while another link agrees with the
 * season total — is a franchise-wide/compilation link (Ani-One "บลีช…สงครามเลือดพันปี" holds all
 * 4 TYBW parts, 49 eps, under Part 1's exact title). Its count must not become this season's.
 * The "another link agrees" check keeps single-link long runners (196 eps) safe.
 */
export function isCompilationCount(ep: number, seasonTotal: number, otherEps: number[]): boolean {
  return (
    seasonTotal > 0 &&
    ep > seasonTotal * 2 &&
    otherEps.some((e) => e > 0 && e <= seasonTotal)
  );
}

/** Drop compilation links (see isCompilationCount) from saved entries; idempotent, runs every load */
export function dropCompilationSources(list: Anime[]): { list: Anime[]; changed: boolean } {
  let changed = false;
  const next = list.map((a) => {
    const total = Number(a.totalEp) || 0;
    const eps = (a.sources || []).map((s) => Number(s.latestEp) || 0);
    const sources = (a.sources || []).filter(
      (s, i) => !isCompilationCount(eps[i], total, eps.filter((_, j) => j !== i))
    );
    if (sources.length === (a.sources || []).length) return a;
    changed = true;
    const latestEp = Math.max(0, ...sources.map((s) => Number(s.latestEp) || 0));
    return {
      ...a,
      sources,
      latestEp: latestEp || Math.min(Number(a.latestEp) || 0, total),
      episodes: [], // may hold the compilation's episodes; the detail popup re-fetches
    };
  });
  return { list: next, changed };
}

/** Tag a dub link so it shows as e.g. "Anime-Waku (พากย์ไทย)" next to the sub link */
export function withSourceAudio<T extends { url: string; name: string; audio?: string }>(src: T): T {
  const audio = getSourceAudio(src);
  const name = audio === 'พากย์ไทย' && !/พากย์/.test(src.name) ? `${src.name} (พากย์ไทย)` : src.name;
  return { ...src, audio, name };
}

/**
 * Status / schedule text that platforms put where a Thai title would go ("จบแล้ว",
 * "อัพเดทเวลา 22:00 ทุกวันเสาร์", "วันจันทร์ เวลา 20:00 น. • ตอนที่ 3", "ตอนใหม่ทุกวัน…").
 */
export function isGenericThaiTitle(t?: string): boolean {
  const s = (t || '').trim();
  return (
    !s ||
    /^ตอนใหม่ทุก/.test(s) ||
    /^วัน(?:จันทร์|อังคาร|พุธ|พฤหัส|ศุกร์|เสาร์|อาทิตย์)\S*\s+เวลา/.test(s) ||
    /^(?:อัพ?เดท|อัปเดต)/.test(s) ||
    /อัปเดตแล้ว/.test(s) ||
    /^จบแล้ว$/.test(s) ||
    /^(?:▶️|📼|🍂|🌞|🍛)/u.test(s)
  );
}

/** Saved entries whose Thai title is really status/schedule text: clear it (idempotent) */
export function clearGenericThaiTitles(list: Anime[]): { list: Anime[]; changed: boolean } {
  let changed = false;
  const next = list.map((a) => {
    if (!a.thaiTitle || !isGenericThaiTitle(a.thaiTitle)) return a;
    changed = true;
    return { ...a, thaiTitle: '' };
  });
  return { list: next, changed };
}

// Same Thai name spelled differently by different sites
const THAI_SPELLING_VARIANTS: Array<[RegExp, string]> = [
  [/รีเซ็ต/g, 'รีเซท'],
  // Ani-One writes "ตำรวจคู่หูพลังจิด"; everyone else "…พลังจิต"
  [/พลังจิด/g, 'พลังจิต'],
];

// Stylised letters in logos-as-titles ("PSYЯEN", "ΛLICE") read as their Latin look-alikes
const LOOKALIKE_LETTERS: Record<string, string> = {
  я: 'r',
  и: 'n',
  ø: 'o',
  λ: 'a',
  '∀': 'a',
  σ: 'e',
  ß: 'ss',
};

const COMBINING_ACCENTS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');

export function normalizeAnimeTitle(str?: string): string {
  if (!str) return '';
  let text = str;
  for (const [variant, canonical] of THAI_SPELLING_VARIANTS) text = text.replace(variant, canonical);
  return text
    // é → e (Caramélise); NFD leaves Thai vowels alone
    .normalize('NFD')
    .replace(COMBINING_ACCENTS, '')
    .toLowerCase()
    .replace(/[яиøλ∀σß]/g, (ch) => LOOKALIKE_LETTERS[ch] || ch)
    .replace(/^รวมตอน\s+/i, '')
    .replace(/\s*\[(ซับไทย|พากย์ไทย)\]/gi, '')
    .replace(/\s*\((ซับไทย|พากย์ไทย)\)/gi, '')
    .replace(/【[^】]*】/g, '')
    // Ani-One numbers later parts with a trailing "-N" ("รวมตอน บลีช เทพมรณะ สงครามเลือดพันปี -3")
    .replace(/\s+-\s*([2-9])\s*$/, ' s$1')
    .replace(/(\d+)(?:st|nd|rd|th)\s*season/gi, ' s$1')
    .replace(/\s*(?:season|ซีซั่?น|ภาค(?:ที่)?|part|ss|s)\s*(\d+)/gi, ' s$1')
    .replace(/\((?:season|ซีซั่?น|ภาค(?:ที่)?|part|ss|s)?\s*(\d+)\)/gi, ' s$1')
    .replace(/[^a-z0-9\u0E00-\u0E7F]/g, '')
    .trim();
}

const TITLE_SYNONYMS: Record<string, string> = {
  douluo: 'ถังซาน',
  soulland: 'ถังซาน',
  jueshi: 'สำนักถัง',
  swallowedstar: 'มหาศึกล้างพิภพ',
  mortal: 'คัมภีร์วิถีเซียน',
  perfectworld: 'โลกสมบูรณ์แบบ',
  renegadeimmortal: 'ฝืนลิขิตฟ้า',
  onepiece: 'วันพีช',
  conan: 'โคนัน',
  rezero: 'รีเซทชีวิต',
  iruma: 'อิรุมะ',
  tensura: 'เกิดใหม่ทั้งทีก็เป็นสไลม์',
  slimedattaken: 'เกิดใหม่ทั้งทีก็เป็นสไลม์',
  blackclover: 'แบล็คโคลเวอร์',
  clover: 'แบล็คโคลเวอร์',
  raywhite: 'เรย์ไวท์',
  hyouken: 'เรย์ไวท์',
  battlespirits: 'แบทเทิลสปิริตส์',
  // Keep keywords specific: a bare 'goblin' would merge Goblin Slayer with Reborn as a Goblin
  tenseigoblin: 'เกิดใหม่เป็นก็อบลิน',
  rebornasagoblin: 'เกิดใหม่เป็นก็อบลิน',
  goblinslayer: 'ก็อบลินสเลเยอร์',
  aoashi: 'แข้งเด็กหัวใจนักสู้',
  darkmachine: 'ดาร์กแมชชีน',
  magicknight: 'เมจิกไนท์',
  kurumi: 'คุรุมิ',
  fxfighter: 'คุรุมิ',
  chojo: 'ตำรวจคู่หูพลังจิด',
  vermilionmask: 'หน้ากากสีชาด',
  magicalgirlraising: 'สาวน้อยเวทมนตร์',
  bleach: 'บลีช',
  youjosenki: 'บันทึกสงครามของยัยเผด็จการ',
  tanya: 'บันทึกสงครามของยัยเผด็จการ',
  kaijuno8: 'ไคจูหมายเลข8',
  kaiju8: 'ไคจูหมายเลข8',
  gintama: 'กินทามะ',
  oshinoko: 'เกิดใหม่เป็นลูกโอชิ',
};

const THAI_CHARS = /[฀-๿]/;

/**
 * One normalized title contains the other. That's the same show only when the extra part is in
 * the other script — a translation tacked on ("romeliasenki" + "บันทึกสงครามโรเมเลีย"). Extra text
 * in the same script is an arc/part name ("bleachsennenkessenhen" + "kashintan",
 * "บลีช…พันปี" + "บทภัยพิบัติ"), i.e. a different part of the franchise.
 */
function isSameShowBySubstring(na: string, nb: string): boolean {
  const [shorter, longer] = na.length <= nb.length ? [na, nb] : [nb, na];
  if (shorter.length < 6 || !longer.includes(shorter)) return false;
  const extra = longer.replace(shorter, '').replace(/s\d+/g, '').replace(/\d+/g, '');
  if (extra.length <= 2) return true;
  return THAI_CHARS.test(shorter) ? !THAI_CHARS.test(extra) : !/[a-z]/.test(extra);
}

/**
 * A show's names that don't say the season inherit it from one that does: "Tokyo Revengers
 * Santen Sensou-hen" saved with Thai "(ภาค4)" is season 4, not season 1
 */
function withShowSeason(normalizedTitles: string[]): string[] {
  const season = normalizedTitles.map((t) => t.match(/s(\d+)/)?.[1]).find(Boolean);
  if (!season || season === '1') return normalizedTitles;
  return normalizedTitles.map((t) => (/s\d+/.test(t) ? t : `${t}s${season}`));
}

function seasonNumberOf(normalizedTitle: string): string {
  const m = normalizedTitle.match(/s(\d+)/);
  return m ? String(parseInt(m[1], 10)) : '1';
}

export function areAnimeMatching(
  a: {
    id?: string;
    title?: string;
    thaiTitle?: string;
    romajiTitle?: string;
    displayTitle?: string;
    fullTitle?: string;
    sources?: { url?: string; name?: string }[];
  },
  b: {
    id?: string;
    title?: string;
    thaiTitle?: string;
    romajiTitle?: string;
    displayTitle?: string;
    fullTitle?: string;
    sources?: { url?: string; name?: string }[];
  }
): boolean {
  if (a.id && b.id && a.id === b.id) return true;
  // If they share exact source URL
  const sourcesA = a.sources || [];
  const sourcesB = b.sources || [];
  if (sourcesA.some((sa) => sourcesB.some((sb) => sa.url && sb.url && sa.url === sb.url))) {
    return true;
  }

  const titlesA = [a.title, a.thaiTitle, a.romajiTitle, a.displayTitle, a.fullTitle]
    .filter(Boolean) as string[];
  const titlesB = [b.title, b.thaiTitle, b.romajiTitle, b.displayTitle, b.fullTitle]
    .filter(Boolean) as string[];

  const normA = withShowSeason(titlesA.map(normalizeAnimeTitle).filter(Boolean));
  const normB = withShowSeason(titlesB.map(normalizeAnimeTitle).filter(Boolean));

  for (const na of normA) {
    for (const nb of normB) {
      if (na === nb) return true;
      // Never merge different seasons; a title without a season number counts as season 1
      // (otherwise "Ao Ashi" (2022) swallows "Aoashi Season 2")
      if (seasonNumberOf(na) !== seasonNumberOf(nb)) continue;
      if (isSameShowBySubstring(na, nb)) return true;
      // Synonyms bridge the English and Thai names, but the rest of the title must still agree:
      // "bleach…" vs "บลีช…บทภัยพิบัติ" are different parts of one franchise, not the same show
      for (const [enKey, thKey] of Object.entries(TITLE_SYNONYMS)) {
        const normEn = normalizeAnimeTitle(enKey);
        const normTh = normalizeAnimeTitle(thKey);
        const hasKey = (t: string) => t.includes(normEn) || t.includes(normTh);
        if (!hasKey(na) || !hasKey(nb)) continue;
        const canon = (t: string) => t.replace(normEn, '§').replace(normTh, '§');
        const ca = canon(na);
        const cb = canon(nb);
        if (ca === cb || isSameShowBySubstring(ca, cb)) return true;
      }
    }
  }
  return false;
}

export function mergeDuplicateAnime(list: Anime[]): { mergedList: Anime[]; mergedCount: number } {
  const result: Anime[] = [];
  let mergedCount = 0;

  for (const item of list) {
    const existingIndex = result.findIndex((r) => areAnimeMatching(r, item));
    if (existingIndex >= 0) {
      // Merge into existing
      mergedCount++;
      const target = result[existingIndex];
      const combinedSources = [...target.sources];
      for (const s of item.sources) {
        if (!combinedSources.some((cs) => isDuplicateSource(cs, s))) {
          combinedSources.push(withSourceAudio(s));
        }
      }

      // Filter out Netflix completely per user request
      const filteredSources = combinedSources.filter(
        (s) =>
          s.type !== 'netflix' &&
          !(s.name && s.name.toLowerCase().includes('netflix')) &&
          !(s.url && s.url.toLowerCase().includes('netflix'))
      );

      // Pick highest latestEp
      let latestEp = Math.max(Number(target.latestEp) || 0, Number(item.latestEp) || 0);

      // Pick best imageUrl (prefer MAL poster cdn.myanimelist.net or longer URL)
      const bestImg =
        target.imageUrl.includes('cdn.myanimelist.net')
          ? target.imageUrl
          : item.imageUrl.includes('cdn.myanimelist.net')
          ? item.imageUrl
          : target.imageUrl || item.imageUrl;

      // Pick best schedule using compareBroadcastSchedule (earliest, tie-broken by Ani-One)
      const scheduleCandidates = [
        {
          broadcastDay: target.broadcastDay,
          broadcastTime: target.broadcastTime,
          platform: target.sources?.[0]?.type,
        },
        {
          broadcastDay: item.broadcastDay,
          broadcastTime: item.broadcastTime,
          platform: item.sources?.[0]?.type,
        },
      ];
      scheduleCandidates.sort(compareBroadcastSchedule);
      let broadcastDay = (scheduleCandidates[0].broadcastDay as BroadcastDay) || target.broadcastDay;
      let broadcastTime = scheduleCandidates[0].broadcastTime || target.broadcastTime;

      // Re-order sources so that winning platform is primary
      const winPlatform = scheduleCandidates[0].platform;
      if (winPlatform) {
        const winIdx = filteredSources.findIndex((s) => s.type === winPlatform);
        if (winIdx > 0) {
          const [w] = filteredSources.splice(winIdx, 1);
          filteredSources.unshift(w);
        }
      }

      // Specific known schedules:
      // 1. Black Clover: Bilibili broadcast is Saturday 22:00, latest episode 3
      const isBlackClover =
        target.title.toLowerCase().includes('black clover') ||
        target.title.includes('แบล็คโคลเวอร์') ||
        item.title.toLowerCase().includes('black clover') ||
        item.title.includes('แบล็คโคลเวอร์') ||
        (target.thaiTitle && target.thaiTitle.includes('แบล็คโคลเวอร์')) ||
        (item.thaiTitle && item.thaiTitle.includes('แบล็คโคลเวอร์'));

      let cleanTitle = target.title;
      let cleanThaiTitle = target.thaiTitle;

      // Replace generic schedule text with proper Thai title
      const isGeneric = (t?: string) => !t || /^ตอนใหม่ทุก/i.test(t) || /^วัน(?:จันทร์|อังคาร|พุธ|พฤหัส|ศุกร์|เสาร์|อาทิตย์)/i.test(t);
      if (isGeneric(cleanThaiTitle) && !isGeneric(item.thaiTitle)) {
        cleanThaiTitle = item.thaiTitle;
      }

      if (isBlackClover) {
        cleanTitle = 'Black Clover Season 2';
        cleanThaiTitle = 'แบล็คโคลเวอร์ ภาคที่ 2';
        broadcastDay = 'SAT';
        broadcastTime = '22:00';
        latestEp = Math.max(latestEp, 3);
        target.totalEp = 26; // Current season only! (Not cumulative 196)
      }

      // 2. Ray White: Ani-One & Bilibili broadcast is Thursday 20:00, latest episode 7
      if (
        target.title.includes('เรย์ไวท์') ||
        target.title.toLowerCase().includes('iceblade') ||
        item.title.includes('เรย์ไวท์')
      ) {
        broadcastDay = 'THU';
        broadcastTime = '20:00';
        latestEp = Math.max(latestEp, 7);
      }

      const resolvedTotal = resolveAnimeTotalEpisodes(
        `${cleanTitle} ${cleanThaiTitle}`,
        latestEp,
        target.status || item.status,
        target.totalEp || item.totalEp
      );
      const finalTotalEp =
        isBlackClover
          ? 26
          : typeof resolvedTotal === 'number' && resolvedTotal > 0
          ? resolvedTotal
          : (target.totalEp || item.totalEp || 0);

      result[existingIndex] = {
        ...target,
        title: cleanTitle,
        thaiTitle: cleanThaiTitle,
        latestEp,
        totalEp: finalTotalEp,
        watchedEp: 0, // Reset watched to 0 per user request
        imageUrl: bestImg,
        broadcastDay: broadcastDay || 'UNKNOWN',
        broadcastTime: broadcastTime || '20:00',
        sources: filteredSources,
        episodes:
          (target.episodes?.length || 0) >= (item.episodes?.length || 0)
            ? target.episodes
            : item.episodes,
      };
    } else {
      const cleanSources = item.sources.filter(
        (s) =>
          s.type !== 'netflix' &&
          !(s.name && s.name.toLowerCase().includes('netflix')) &&
          !(s.url && s.url.toLowerCase().includes('netflix'))
      );

      let bDay = item.broadcastDay;
      let bTime = item.broadcastTime;
      let lEp = item.latestEp;
      let cTitle = item.title;
      let cThaiTitle = item.thaiTitle;
      let tEp = item.totalEp;

      if (
        item.title.toLowerCase().includes('black clover') ||
        (item.thaiTitle && item.thaiTitle.includes('แบล็คโคลเวอร์'))
      ) {
        cTitle = 'Black Clover Season 2';
        cThaiTitle = 'แบล็คโคลเวอร์ ภาคที่ 2';
        bDay = 'SAT';
        bTime = '22:00';
        lEp = Math.max(lEp || 1, 3);
        tEp = 26;
      }

      if (
        item.title.includes('เรย์ไวท์') ||
        item.title.toLowerCase().includes('iceblade')
      ) {
        bDay = 'THU';
        bTime = '20:00';
        lEp = Math.max(lEp || 1, 7);
      }

      result.push({
        ...item,
        title: cTitle,
        thaiTitle: cThaiTitle,
        broadcastDay: bDay,
        broadcastTime: bTime,
        latestEp: lEp,
        totalEp: tEp,
        watchedEp: 0, // Reset watched to 0 per user request
        sources: cleanSources,
      });
    }
  }

  return { mergedList: result, mergedCount };
}

export function getPlatformBadgeStyle(type: PlatformType): {
  bg: string;
  icon: string;
  label: string;
} {
  switch (type) {
    case 'youtube':
      return {
        bg: 'bg-red-600/25 text-red-200 border-red-500/40',
        icon: '▶️',
        label: 'Ani-One',
      };
    case 'anime-waku':
      return {
        bg: 'bg-teal-600/25 text-teal-200 border-teal-500/40',
        icon: '🌐',
        label: 'Anime-Waku',
      };
    case 'animeruka':
      return {
        bg: 'bg-orange-600/25 text-orange-200 border-orange-500/40',
        icon: '🟠',
        label: 'AnimeRuka',
      };
    case 'bilibili':
      return {
        bg: 'bg-sky-600/25 text-sky-200 border-sky-500/40',
        icon: '📺',
        label: 'Bilibili',
      };
    case 'wetv':
      return {
        bg: 'bg-emerald-600/25 text-emerald-200 border-emerald-500/40',
        icon: '🟢',
        label: 'WeTV',
      };
    default:
      return {
        bg: 'bg-slate-700/40 text-slate-200 border-slate-600/50',
        icon: '🔗',
        label: 'Web',
      };
  }
}

export function getAnimeWatchStatus(anime: Anime): {
  label: string;
  badgeClass: string;
  isCaughtUp: boolean;
} {
  const watched = Number(anime.watchedEp) || 0;
  const latest = Number(anime.latestEp) || 0;
  const diff = Math.max(0, latest - watched);

  if (anime.status === 'รอฉาย') {
    return {
      label: 'รอฉาย',
      badgeClass: 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30',
      isCaughtUp: false,
    };
  }

  if (anime.status === 'จบแล้ว' && watched >= latest && latest > 0) {
    return {
      label: 'ดูจบแล้ว ✓',
      badgeClass: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
      isCaughtUp: true,
    };
  }

  if (diff === 0 && latest > 0) {
    return {
      label: 'ดูทันล่าสุด',
      badgeClass: 'bg-teal-500/20 text-teal-300 border border-teal-500/30',
      isCaughtUp: true,
    };
  }

  if (diff > 0) {
    return {
      label: `ค้าง ${diff} ตอน`,
      badgeClass: 'bg-amber-500/20 text-amber-300 border border-amber-500/40',
      isCaughtUp: false,
    };
  }

  return {
    label: anime.status || 'กำลังฉาย',
    badgeClass: 'bg-slate-800 text-slate-300 border border-slate-700',
    isCaughtUp: false,
  };
}

export function getDayConfig(day: BroadcastDay) {
  return BROADCAST_DAYS.find((d) => d.id === day) || BROADCAST_DAYS[BROADCAST_DAYS.length - 1];
}

const KNOWN_SEASONAL_EPISODES: Array<{ regex: RegExp; total: number }> = [
  {
    regex:
      /douluo.*(?:ii|\b2\b|ภาค\s*2|jueshi)|soul\s*land.*(?:ii|\b2\b|ภาค\s*2|jueshi)|ถังซาน.*(?:ภาค\s*2|2(?!\d)|สำนักถัง)/i,
    total: 182,
  },
  { regex: /black\s*clover|แบล็คโคลเวอร์/i, total: 26 },
  { regex: /re:zero|รีเซทชีวิต/i, total: 16 },
  { regex: /bleach|บลีช/i, total: 13 },
  { regex: /tokyo\s*revengers/i, total: 13 },
  { regex: /iruma|อิรุมะคุง/i, total: 21 },
  { regex: /slime|สไลม์/i, total: 24 },
  { regex: /kusuriya|หมอยา/i, total: 24 },
];

const KNOWN_LONG_RUNNERS =
  /douluo|ถังซาน|swallowed\s*star|มหาศึกล้างพิภพ|mortal|คัมภีร์วิถีเซียน|perfect\s*world|โลกสมบูรณ์แบบ|shrouding|พิชิตสวรรค์|herding\s*gods|ตำนานเทพกู้จักรวาล|renegade\s*immortal|ฝืนลิขิตฟ้า|battle\s*through\s*the\s*heavens|สัประยุทธ์ทะลุฟ้า|one\s*piece|วันพีช|conan|โคนัน/i;

export function resolveAnimeTotalEpisodes(
  title: string,
  latestEp = 1,
  status?: string,
  rawTotalEp?: number
): number | 'ONGOING_LONG' {
  // If rawTotalEp is valid and >= latestEp, use it
  if (typeof rawTotalEp === 'number' && rawTotalEp > 0) {
    if (status === 'จบแล้ว' || rawTotalEp >= latestEp) {
      return rawTotalEp;
    }
  }

  // 1. Check title patterns for finished episode count e.g. "ตอนที่ 1-265 ... จบแล้ว" or "ตอนที่ 1-12 จบแล้ว"
  const finishedMatch = title.match(/ตอนที่\s*\d+\s*-\s*(\d+)\s*(?:ซับไทย|พากย์ไทย)?\s*จบแล้ว/i);
  if (finishedMatch) {
    const epCount = parseInt(finishedMatch[1], 10);
    if (epCount > 0) return epCount;
  }

  // 2. Check title patterns for explicit count e.g. "รวม 12 ตอน" or "12 ตอนจบ" or "(26 ตอน)"
  const titleMatch = title.match(/(?:รวม|ทั้งหมด|\/|\()\s*(\d+)\s*ตอน/i) || title.match(/全(\d+)话/i);
  if (titleMatch) {
    const parsed = parseInt(titleMatch[1], 10);
    if (parsed > 0 && (parsed >= latestEp || status === 'จบแล้ว')) return parsed;
  }

  // 3. If finished and latestEp > 0, the latest episode IS the total episode count!
  const isFinished = status === 'จบแล้ว' || (/จบแล้ว/.test(title) && !/ยังไม่จบ/.test(title));
  if (isFinished && latestEp > 0) {
    return latestEp;
  }

  // 4. Known specific seasonal anime with fixed announced counts
  for (const entry of KNOWN_SEASONAL_EPISODES) {
    if (entry.regex.test(title)) {
      if (latestEp <= entry.total) {
        return entry.total;
      }
      return 'ONGOING_LONG';
    }
  }

  // 5. Long-running continuous ongoing series
  if (KNOWN_LONG_RUNNERS.test(title)) {
    return 'ONGOING_LONG';
  }

  // 6. If airing and latest episode is already > 24 without a known season count, it's continuous
  if (latestEp > 24) {
    if (/eternal\s*supreme|จ้าวยุทธ์อมตะนิรันดร์กาล/i.test(title) && latestEp <= 52) {
      return 52;
    }
    return 'ONGOING_LONG';
  }

  // 7. If current episode is between 13 and 24, it's typically a 2-cours 24-episode season
  if (latestEp > 12) {
    return 24;
  }

  // 8. Standard 1-cour Japanese seasonal TV anime default is 12 episodes
  return 12;
}

export function formatAnimeEpisodeStatus(item: {
  romajiTitle?: string;
  thaiTitle?: string;
  displayTitle?: string;
  fullTitle?: string;
  latestEp?: number;
  totalEp?: number;
  status?: string;
}) {
  const combinedTitle = `${item.fullTitle || ''} ${item.romajiTitle || ''} ${item.displayTitle || ''} ${item.thaiTitle || ''}`;
  const latest = Number(item.latestEp) || 0;

  // NEVER assume finished if title says "ยังไม่จบ" or item.status === 'กำลังฉาย'!
  const isExplicitlyAiring = item.status === 'กำลังฉาย' || /ยังไม่จบ/i.test(combinedTitle);
  const isFinished = !isExplicitlyAiring && (item.status === 'จบแล้ว' || /จบแล้ว/i.test(combinedTitle));

  const total = resolveAnimeTotalEpisodes(combinedTitle, latest, isFinished ? 'จบแล้ว' : 'กำลังฉาย', item.totalEp);

  if (total === 'ONGOING_LONG') {
    return {
      badgeText: latest > 0 ? `ตอนที่ ${latest} (ต่อเนื่อง)` : 'ฉายต่อเนื่อง',
      badgeClass: 'bg-indigo-600/95 text-white',
      totalLabel: 'ฉายต่อเนื่องระยะยาว',
      currentLabel: latest > 0 ? `ปัจจุบันตอนที่ ${latest}` : 'กำลังฉาย',
      isOngoingLong: true,
      totalCount: 0,
    };
  }

  if (isFinished) {
    const finalCount = total > 0 ? Math.max(total, latest) : latest;
    return {
      badgeText: finalCount > 0 ? `${finalCount} ตอนจบ` : 'จบแล้ว',
      badgeClass: 'bg-emerald-600/95 text-white',
      totalLabel: `มีทั้งหมด ${finalCount} ตอน`,
      currentLabel: 'ฉายจบแล้ว',
      isOngoingLong: false,
      totalCount: finalCount,
    };
  }

  // Airing series
  const actualTotal = Math.max(total, latest);
  return {
    badgeText: `ฉายแล้ว ${latest}/${actualTotal} ตอน`,
    badgeClass: 'bg-rose-600/95 text-white',
    totalLabel: `มีทั้งหมด ${actualTotal} ตอน`,
    currentLabel: `ฉายแล้ว ${latest} ตอน`,
    isOngoingLong: false,
    totalCount: actualTotal,
  };
}

const DAY_ORDER: Record<string, number> = {
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
  SUN: 7,
  UNKNOWN: 99,
};

export function compareBroadcastSchedule(
  a: { broadcastDay?: BroadcastDay | string; broadcastTime?: string; platform?: string },
  b: { broadcastDay?: BroadcastDay | string; broadcastTime?: string; platform?: string }
): number {
  const dayOrderA = DAY_ORDER[a.broadcastDay || 'UNKNOWN'] || 99;
  const dayOrderB = DAY_ORDER[b.broadcastDay || 'UNKNOWN'] || 99;

  // 1. If one has known day and other is unknown or earlier in week
  if (dayOrderA !== dayOrderB) {
    return dayOrderA - dayOrderB;
  }

  // 2. Same day: compare time (e.g. '19:30' < '20:00')
  const timeA = a.broadcastTime?.trim() || '99:99';
  const timeB = b.broadcastTime?.trim() || '99:99';
  const timeDiff = timeA.localeCompare(timeB);
  if (timeDiff !== 0) {
    return timeDiff;
  }

  // 3. Same day and same time:
  // User explicit instruction: "แต่ถ้าอัพเวลาเหมือนกันให้เอาลิงค์ของ ani-one ไปใส่แทน"
  const isAniOneA = a.platform === 'youtube';
  const isAniOneB = b.platform === 'youtube';
  if (isAniOneA && !isAniOneB) return -1;
  if (!isAniOneA && isAniOneB) return 1;

  // Secondary tie-breaker: Bilibili before other
  const isBiliA = a.platform === 'bilibili';
  const isBiliB = b.platform === 'bilibili';
  if (isBiliA && !isBiliB) return -1;
  if (!isBiliA && isBiliB) return 1;

  return 0;
}

export function enrichAnimeSourcesAndSchedule(
  target: Anime | WakuFeedItem,
  feedPool: WakuFeedItem[]
): {
  sources: AnimeSource[];
  broadcastDay: BroadcastDay;
  broadcastTime: string;
  bestTitle: string;
  bestThaiTitle: string;
  bestImageUrl: string;
  latestEp: number;
  totalEp: number;
  detectedDayNote: string;
} {
  // Build combined sources list
  const existingSources: AnimeSource[] =
    'sources' in target && Array.isArray(target.sources) ? [...target.sources] : [];

  // Skip compilation links that cover more seasons than this one (see isCompilationCount)
  const seasonTotal = Number(target.totalEp) || 0;
  const knownEps = [Number(target.latestEp) || 0, ...existingSources.map((s) => Number(s.latestEp) || 0)];
  const matchedFeeds = feedPool.filter(
    (f) =>
      areAnimeMatching(target, f) &&
      !isCompilationCount(Number(f.latestEp) || 0, seasonTotal, knownEps)
  );

  const addSourceIfMissing = (src: AnimeSource) => {
    // One link per platform per audio track (keeps both the sub and the dub page)
    if (!existingSources.some((s) => isDuplicateSource(s, src))) {
      existingSources.push(withSourceAudio(src));
    }
  };

  const targetUrl = 'url' in target && typeof target.url === 'string' ? target.url : undefined;
  const targetPlatform: PlatformType =
    ('platform' in target && target.platform ? (target.platform as PlatformType) : undefined) ||
    ('sources' in target && target.sources?.[0]?.type ? target.sources[0].type : undefined) ||
    (targetUrl ? detectPlatformFromUrl(targetUrl).type : 'other');

  // Add target's own source if it has a URL
  if (targetUrl) {
    const tBadge = getPlatformBadgeStyle(targetPlatform);
    addSourceIfMissing({
      type: targetPlatform,
      name: tBadge.label,
      url: targetUrl,
      latestEp: target.latestEp || 1,
    });
  }

  // Add all matched feeds
  for (const feed of matchedFeeds) {
    const pType = feed.platform || detectPlatformFromUrl(feed.url).type;
    const badge = getPlatformBadgeStyle(pType);
    addSourceIfMissing({
      type: pType,
      name: badge.label,
      url: feed.url,
      latestEp: feed.latestEp || 1,
    });
  }

  // Filter out Netflix completely per user request
  const filteredSources = existingSources.filter(
    (s) =>
      s.type !== 'netflix' &&
      !(s.name && s.name.toLowerCase().includes('netflix')) &&
      !(s.url && s.url.toLowerCase().includes('netflix'))
  );

  // Candidates for best schedule: target + all matched feeds
  const candidates: Array<{
    broadcastDay?: BroadcastDay | string;
    broadcastTime?: string;
    platform?: string;
    url?: string;
    name?: string;
  }> = [];

  if (target.broadcastDay && target.broadcastDay !== 'UNKNOWN') {
    candidates.push({
      broadcastDay: target.broadcastDay,
      broadcastTime: target.broadcastTime,
      platform: targetPlatform,
      url: targetUrl || ('sources' in target ? target.sources?.[0]?.url : undefined),
    });
  }

  for (const f of matchedFeeds) {
    if (f.broadcastDay && f.broadcastDay !== 'UNKNOWN') {
      candidates.push({
        broadcastDay: f.broadcastDay,
        broadcastTime: f.broadcastTime,
        platform: f.platform,
        url: f.url,
      });
    }
  }

  // Sort candidates by earliest schedule with Ani-One tie-breaker
  candidates.sort(compareBroadcastSchedule);

  let bestDay: BroadcastDay = 'UNKNOWN';
  let bestTime = '';
  let winningPlatform = '';

  if (candidates.length > 0) {
    bestDay = (candidates[0].broadcastDay as BroadcastDay) || 'UNKNOWN';
    bestTime = candidates[0].broadcastTime || '';
    winningPlatform = candidates[0].platform || '';
  } else if ('broadcastDay' in target && target.broadcastDay) {
    bestDay = target.broadcastDay;
    bestTime = target.broadcastTime || '';
  }

  // Re-order filteredSources so that winning platform is placed at index 0 (primary link)
  if (winningPlatform) {
    const winningIdx = filteredSources.findIndex((s) => s.type === winningPlatform);
    if (winningIdx > 0) {
      const [winSrc] = filteredSources.splice(winningIdx, 1);
      filteredSources.unshift(winSrc);
    }
  }

  // Find best titles:
  // For Thai title: ignore generic broadcast text like "ตอนใหม่ทุกวันจันทร์..." or "วันจันทร์ เวลา 20:00 น."
  let bestThaiTitle = ('thaiTitle' in target ? target.thaiTitle : '') || '';
  const isGenericThai = isGenericThaiTitle;

  if (isGenericThai(bestThaiTitle)) {
    for (const f of matchedFeeds) {
      if (f.thaiTitle && !isGenericThai(f.thaiTitle)) {
        bestThaiTitle = f.thaiTitle;
        break;
      }
    }
  }
  // Never keep status/schedule text ("จบแล้ว") as the Thai title
  if (isGenericThai(bestThaiTitle)) bestThaiTitle = '';

  // Best English / Romaji title
  const bestTitle =
    ('title' in target && target.title ? target.title : '') ||
    ('romajiTitle' in target && target.romajiTitle ? target.romajiTitle : '') ||
    ('displayTitle' in target && target.displayTitle ? target.displayTitle : '') ||
    bestThaiTitle;

  // Best Poster Image
  let bestImageUrl = ('imageUrl' in target ? target.imageUrl : '') || '';
  for (const f of [target, ...matchedFeeds]) {
    if (f.imageUrl && f.imageUrl.includes('cdn.myanimelist.net')) {
      bestImageUrl = f.imageUrl;
      break;
    }
  }
  if (!bestImageUrl && matchedFeeds.length > 0) {
    bestImageUrl = matchedFeeds[0].imageUrl || bestImageUrl;
  }

  // Calculate highest episode
  const allLatestEps = [
    target.latestEp || 1,
    ...matchedFeeds.map((f) => f.latestEp || 1),
    ...filteredSources.map((s) => s.latestEp || 1),
  ];
  const maxLatestEp = Math.max(...allLatestEps, 1);

  // Calculate total episodes using resolveAnimeTotalEpisodes (accurate 182 for Douluo Dalu II, 26 for Black Clover)
  const combinedTitles = `${bestTitle} ${bestThaiTitle} ${'fullTitle' in target ? target.fullTitle : ''} ${matchedFeeds.map((f) => f.fullTitle || f.displayTitle).join(' ')}`;
  const resolved = resolveAnimeTotalEpisodes(combinedTitles, maxLatestEp, 'กำลังฉาย', target.totalEp);
  const finalTotal = typeof resolved === 'number' && resolved > 0 ? resolved : (target.totalEp || 12);

  // Detected Day Note
  const dayCfg = getDayConfig(bestDay);
  const pStyle = winningPlatform ? getPlatformBadgeStyle(winningPlatform as PlatformType) : null;
  const defaultNote =
    'detectedDayNote' in target && target.detectedDayNote
      ? target.detectedDayNote
      : 'เพิ่มจากหน้าสำรวจ';
  const detectedDayNote =
    bestDay !== 'UNKNOWN' && bestTime
      ? `ตอนใหม่ทุก${dayCfg.nameTh} เวลา ${bestTime} น. (${pStyle?.label || 'ทางการ'})`
      : defaultNote;

  return {
    sources: filteredSources,
    broadcastDay: bestDay,
    broadcastTime: bestTime,
    bestTitle,
    bestThaiTitle,
    bestImageUrl: formatImageUrl(bestImageUrl),
    latestEp: maxLatestEp,
    totalEp: finalTotal,
    detectedDayNote,
  };
}

